import React, { useState } from "react";
import { Download, Loader2, Megaphone } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

// LytePress brand social ad creatives — preview + one-tap download for the owner to post.
const ADS = [
  {
    key: "square",
    label: "Square · 1:1",
    hint: "Instagram / Facebook feed",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/14dc98a43aaab88c0eed0361a18d2984a3146afff2de0929e67a25ec3c63d89c.jpeg",
  },
  {
    key: "vertical",
    label: "Vertical · 9:16",
    hint: "Stories / Reels / TikTok",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/31ca6bb74dc07a3fb8216a9e7e10054d2f518782f15b5abebc252e227da1570e.jpeg",
  },
  {
    key: "landscape",
    label: "Landscape · 1.91:1",
    hint: "Link ads / banners",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/3e3cfa3d0b496609cd44f62efb0f2e2e3bea67dc8e9709b49ba05c537809f93e.jpeg",
  },
];

// Business-recruitment flyers (join LytePress) — localized + a print-ready PDF with a scannable QR.
const FLYERS = [
  { key: "flyer-en", label: "Flyer · English", hint: "A4 portrait", ext: "jpg",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/a288b70654fc326972f7ecca4576bdea9bebda0e194035bc356957fc8eac16bb.jpeg" },
  { key: "flyer-square", label: "Flyer · Square", hint: "WhatsApp / Instagram 1:1", ext: "jpg",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/66b352457ed764a3fc97be964683f033e2a4be32e3015517dfa17c0809b04463.jpeg" },
  { key: "flyer-es", label: "Flyer · Español", hint: "A4 portrait", ext: "jpg",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/336721f6329ecafc8bae48cf5c1609b81c8e9a46e7b2ccfdfb31b189cd6637a9.jpeg" },
  { key: "flyer-fr", label: "Flyer · Français", hint: "A4 portrait", ext: "jpg",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/92576e07bfcd38b6291a99ffbbfac3c19daa4ae8d293b2b64c48a2460d6bb3f7.jpeg" },
  { key: "flyer-de", label: "Flyer · Deutsch", hint: "A4 portrait", ext: "jpg",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/0d4ab926a01edd4c929d74c2e0116e5e9cff80b6a9e73f6b56ade461c2b89433.jpeg" },
  { key: "flyer-pt", label: "Flyer · Português", hint: "A4 portrait", ext: "jpg",
    url: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/cd051a23df67bd51384b5208db999d679d3e4c415805fd323483e6696bb7e717.jpeg" },
  { key: "flyer-print", label: "Printable PDF · QR", hint: "Scan-to-join, A4 print", ext: "pdf",
    url: "/adkit/lytepress-business-flyer-print.pdf", preview: "/adkit/lytepress-business-flyer-print.png" },
];

export const AdminAdKit = () => {
  const { toast } = useToast();
  const [busy, setBusy] = useState("");

  const download = async (ad) => {
    setBusy(ad.key);
    try {
      const res = await fetch(ad.url, { mode: "cors" });
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `lytepress-ad-${ad.key}.${ad.ext || "jpg"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      // Fallback: open in a new tab so the owner can still save it manually.
      window.open(ad.url, "_blank", "noopener");
      toast({ title: "Opened in a new tab", description: "Long-press or right-click the image to save it." });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-ad-kit">
      <p className="text-base font-extrabold text-foreground flex items-center gap-1.5">
        <Megaphone size={16} className="text-[#16a34a]" /> Ad Kit
      </p>
      <p className="text-sm text-muted-foreground mt-1">
        Ready-to-post LytePress social ads. Download and share on Instagram, Facebook, TikTok or as link ads.
      </p>

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ADS.map((ad) => (
          <div key={ad.key} className="rounded-2xl border border-border overflow-hidden flex flex-col" data-testid={`ad-kit-${ad.key}`}>
            <div className="bg-secondary/40 flex items-center justify-center p-3">
              <img src={ad.url} alt={ad.label} loading="lazy"
                className="max-h-56 w-auto object-contain rounded-lg" />
            </div>
            <div className="p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{ad.label}</p>
                <p className="text-xs text-muted-foreground truncate">{ad.hint}</p>
              </div>
              <button onClick={() => download(ad)} disabled={!!busy} data-testid={`ad-kit-download-${ad.key}`}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white rounded-xl px-3 py-2 transition-colors">
                {busy === ad.key ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} Download
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-base font-extrabold text-foreground flex items-center gap-1.5 mt-7">
        <Megaphone size={16} className="text-[#16a34a]" /> Join LytePress — business flyers
      </p>
      <p className="text-sm text-muted-foreground mt-1">
        Recruit shops to LytePress. Share the localized flyers, or print the PDF — it has a QR code
        that opens the business sign-up page.
      </p>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {FLYERS.map((ad) => (
          <div key={ad.key} className="rounded-2xl border border-border overflow-hidden flex flex-col" data-testid={`ad-kit-${ad.key}`}>
            <div className="bg-secondary/40 flex items-center justify-center p-3">
              <img src={ad.preview || ad.url} alt={ad.label} loading="lazy"
                className="max-h-56 w-auto object-contain rounded-lg" />
            </div>
            <div className="p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{ad.label}</p>
                <p className="text-xs text-muted-foreground truncate">{ad.hint}</p>
              </div>
              <button onClick={() => download(ad)} disabled={!!busy} data-testid={`ad-kit-download-${ad.key}`}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white rounded-xl px-3 py-2 transition-colors">
                {busy === ad.key ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} Download
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminAdKit;
